/**
 * FloatingTransactionPanel — persistent overlay showing recent fills + connection status.
 *
 * Fixed bottom-right, collapsible, persists across views.
 */

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Money } from '@/components/atoms/Money';
import { useActivity, type ActivityEvent, type ActivityEventType } from '@/contexts/ActivityContext';

const COLLAPSE_KEY = 'galactus:txPanelCollapsed';

type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

function deriveConnectionStatus(events: ActivityEvent[]): ConnectionStatus {
  // Walk events (newest first) to find last connection-related event
  for (const e of events) {
    if (e.type === 'connection_restored') return 'connected';
    if (e.type === 'connection_lost') return 'reconnecting';
  }
  return 'connected'; // default: assume connected
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

const statusDot: Record<ConnectionStatus, string> = {
  connected: 'bg-green-500',
  reconnecting: 'bg-yellow-500 animate-pulse',
  disconnected: 'bg-red-500',
};

const statusLabel: Record<ConnectionStatus, string> = {
  connected: 'Connected',
  reconnecting: 'Reconnecting...',
  disconnected: 'Disconnected',
};

function eventTypeLabel(type: ActivityEventType): string {
  switch (type) {
    case 'order_filled': return 'fill';
    case 'order_placed': return 'new';
    case 'order_cancelled': return 'cxl';
    case 'connection_lost': return 'disc';
    case 'connection_restored': return 'conn';
    case 'connection_change': return 'conn';
    case 'error': return 'err';
    case 'queue_velocity': return 'vel';
  }
}

function eventTypeColor(type: ActivityEventType): string {
  switch (type) {
    case 'order_filled': return 'text-emerald-400';
    case 'order_placed': return 'text-blue-400';
    case 'order_cancelled': return 'text-amber-400';
    case 'connection_lost': return 'text-red-400';
    case 'connection_restored': return 'text-emerald-400';
    case 'connection_change': return 'text-zinc-400';
    case 'error': return 'text-red-400';
    case 'queue_velocity': return 'text-zinc-500';
  }
}

export function FloatingTransactionPanel() {
  const { events } = useActivity();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Persist collapse state
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {
      // noop
    }
  }, [collapsed]);

  // Show all transaction-relevant events (exclude noisy queue_velocity)
  const txEvents = events.filter((e) => e.type !== 'queue_velocity');
  const connectionStatus = deriveConnectionStatus(events);

  // Auto-scroll to top on new events
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [txEvents.length]);

  return (
    <div
      className={cn(
        'bg-card/95 border-border fixed bottom-12 right-4 z-50 flex flex-col rounded-lg border shadow-xl backdrop-blur-sm',
        collapsed ? 'w-64' : 'w-80'
      )}
      style={{ maxHeight: collapsed ? undefined : '400px' }}
    >
      {/* Header */}
      <div
        className="border-border flex cursor-pointer items-center justify-between border-b px-3 py-2"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <h3 className="text-foreground text-xs font-semibold uppercase tracking-wider">
            Transactions
          </h3>
          <span className="text-muted-foreground text-xs tabular-nums">{txEvents.length}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {collapsed ? (
            <ChevronUp className="text-muted-foreground h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
          )}
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <>
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto"
            style={{ maxHeight: '320px' }}
          >
            {txEvents.length === 0 ? (
              <div className="text-muted-foreground px-3 py-6 text-center text-xs">
                No activity yet
              </div>
            ) : (
              txEvents.map((event) => (
                <div
                  key={event.id}
                  className="border-border/30 flex items-center gap-2 border-b px-3 py-1.5 text-xs"
                >
                  <span className={cn('flex-shrink-0 font-mono text-[10px] uppercase', eventTypeColor(event.type))}>
                    {eventTypeLabel(event.type)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="text-foreground font-medium">{event.message}</span>
                    {event.amount !== undefined && (
                      <>
                        <span className="text-muted-foreground mx-1">·</span>
                        <Money
                          value={event.amount}
                          className="text-xs"
                        />
                      </>
                    )}
                  </div>
                  <span className="text-muted-foreground flex-shrink-0 whitespace-nowrap">
                    {formatTime(event.timestamp)}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Connection status bar */}
          <div className="border-border flex items-center gap-2 border-t px-3 py-1.5">
            <div className={cn('h-2 w-2 rounded-full', statusDot[connectionStatus])} />
            <span className="text-muted-foreground text-xs">{statusLabel[connectionStatus]}</span>
          </div>
        </>
      )}
    </div>
  );
}
